import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { observer } from 'mobx-react-lite';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import { AttendanceType } from '../../../../../api/types/lecturers/attendance/attendance-type';
import { UserAttendanceRequest } from '../../../../../api/types/lecturers/attendance/user-attendance-request';
import { LectureWithSessions } from '../../../../../api/types/programs/course/lectures/lecture-with-schedule-metadata';
import globalCourseStore from '../../../../../stores/global-course-store';
import globalProgramStore from '../../../../../stores/program-store';
import { updateAttendanceOperations } from '../../lecture-page-dialog/tabs/attendance/operations/update-attendance-operations';
import { startLectureAndRefreshOperation } from '../operations/start-lecture-and-refresh-operation';
import appApi from '../../../../../api/app-api';
import { toast } from 'react-toastify';
import globalUserProfile from '../../../../../stores/global-user-profile';

// Generic lecture info that works for both LectureWithSessions and LectureLecturer
interface LectureInfo {
  programId: number;
  courseId: number;
  lectureId: number;
}

interface StartLectureAttendanceDialogProps {
  visible: boolean;
  lecture: LectureWithSessions | null;
  onHide: () => void;
  // Optional props for professor mode
  lectureInfo?: LectureInfo;
  useProfessorMode?: boolean;
  // When true, only updates attendance (doesn't start the lecture)
  updateAttendanceOnly?: boolean;
}

// Extended type that allows null attendance for UI state
interface AttendanceDataItem {
  studentId: number;
  attendance: AttendanceType | null;
}

export const StartLectureAttendanceDialog: React.FC<StartLectureAttendanceDialogProps> =
  observer(
    ({
      visible,
      lecture,
      onHide,
      lectureInfo,
      useProfessorMode,
      updateAttendanceOnly,
    }) => {
      // State for students (will be populated from store or fetched)
      const [students, setStudents] = useState<UserSummaryType[]>([]);
      const [attendanceData, setAttendanceData] = useState<
        AttendanceDataItem[]
      >([]);
      const [isSaving, setIsSaving] = useState(false);
      const [isLoadingStudents, setIsLoadingStudents] = useState(false);

      // Fetch students when dialog opens (for professor mode)
      useEffect(() => {
        if (visible && useProfessorMode && lectureInfo) {
          if (updateAttendanceOnly) {
            fetchStudentsWithExistingAttendance(
              lectureInfo.programId,
              lectureInfo.courseId,
              lectureInfo.lectureId,
            );
          } else {
            fetchStudents(lectureInfo.programId, lectureInfo.courseId);
          }
        } else if (visible && !useProfessorMode) {
          // Admin mode: get students from course store
          const currentStudents =
            globalCourseStore.getCurrentCourse()?.details?.students ?? [];
          setStudents(currentStudents);
        }
      }, [visible, useProfessorMode, lectureInfo, updateAttendanceOnly]);

      const fetchStudents = async (programId: number, courseId: number) => {
        setIsLoadingStudents(true);
        try {
          const response = await appApi.getCourseDetails(programId, courseId);
          const courseStudents = response.data.body.details.students || [];
          setStudents(courseStudents);
        } catch (error) {
          console.error('Failed to fetch students:', error);
          toast.error('שגיאה בטעינת רשימת התלמידים');
          setStudents([]);
        } finally {
          setIsLoadingStudents(false);
        }
      };

      const fetchStudentsWithExistingAttendance = async (
        programId: number,
        courseId: number,
        lectureId: number,
      ) => {
        setIsLoadingStudents(true);
        try {
          const lectureResponse = await appApi.getLectureDetails(
            programId,
            courseId,
            lectureId,
          );
          const existingAttendance = lectureResponse.data.body.attendance || [];

          // Filter out null values and extract students from attendance data
          const validAttendance = existingAttendance.filter(
            (item): item is NonNullable<typeof item> => item !== null,
          );

          const studentsFromAttendance = validAttendance.map(
            (item) => item.student,
          );
          setStudents(studentsFromAttendance);

          // Set existing attendance data (null if no attendance recorded yet)
          setAttendanceData(
            validAttendance.map((item) => ({
              studentId: item.student.id,
              attendance: item.attendance?.attendanceType ?? null,
            })),
          );
        } catch (error) {
          console.error('Failed to fetch lecture details:', error);
          toast.error('שגיאה בטעינת נתוני הנוכחות');
          setStudents([]);
        } finally {
          setIsLoadingStudents(false);
        }
      };

      // Initialize attendance data when students change (skip if updateAttendanceOnly since we already loaded existing data)
      useEffect(() => {
        if (visible && students.length > 0 && !updateAttendanceOnly) {
          setAttendanceData(
            students.map((student) => ({
              studentId: student.id,
              attendance: AttendanceType.Missed,
            })),
          );
          setIsSaving(false);
        }
      }, [visible, students, updateAttendanceOnly]);

      const handleAttendanceChange = (
        studentId: number,
        status: AttendanceType,
      ) => {
        setAttendanceData((prevState) =>
          prevState.map((item) => {
            if (item.studentId === studentId) {
              return { ...item, attendance: status };
            }
            return item;
          }),
        );
      };

      // Filter out null attendance values for API submission
      const getAttendanceDataForApi = (): UserAttendanceRequest[] => {
        return attendanceData.filter(
          (item): item is { studentId: number; attendance: AttendanceType } =>
            item.attendance !== null,
        );
      };

      const handleSave = async () => {
        setIsSaving(true);
        try {
          const attendanceDataForApi = getAttendanceDataForApi();

          if (useProfessorMode && lectureInfo) {
            if (updateAttendanceOnly) {
              // Update attendance only mode: don't start the lecture
              await appApi.upsetLectureAttendance(
                lectureInfo.programId,
                lectureInfo.courseId,
                lectureInfo.lectureId,
                attendanceDataForApi,
              );

              await globalUserProfile.getUserProfile();
              toast.success('הנוכחות עודכנה בהצלחה');
            } else {
              // Professor mode: start lecture and save attendance
              await appApi.startLecture(
                lectureInfo.programId,
                lectureInfo.courseId,
                lectureInfo.lectureId,
              );

              await appApi.upsetLectureAttendance(
                lectureInfo.programId,
                lectureInfo.courseId,
                lectureInfo.lectureId,
                attendanceDataForApi,
              );

              await globalUserProfile.getUserProfile();
              toast.success('ההרצאה החלה והנוכחות נשמרה בהצלחה');
            }
          } else {
            // Admin mode: use existing operations
            if (!lecture) return;

            const programId = globalProgramStore.getCurrentId();
            const courseId = globalCourseStore.getCurrentCourse()?.metadata.id;

            if (!programId || !courseId) return;

            await startLectureAndRefreshOperation(
              programId,
              courseId,
              lecture.item.id,
            );

            await updateAttendanceOperations(
              programId,
              lecture,
              attendanceDataForApi,
            );
          }

          onHide();
        } catch (error) {
          console.error('Error starting lecture or saving attendance:', error);
          // Still close the dialog even if there's an error
          // The operations will show their own error toasts
          onHide();
        } finally {
          setIsSaving(false);
        }
      };

      const attendanceDataModel = students.map((student) => ({
        student,
        attendanceType: attendanceData.find((a) => a.studentId === student.id)
          ?.attendance,
      }));

      // Check if all students have attendance selected
      const allStudentsHaveAttendance =
        students.length > 0 &&
        attendanceData.length === students.length &&
        attendanceData.every((item) => item.attendance !== null);

      const dialogHeader = updateAttendanceOnly
        ? 'עדכון נתוני נוכחות'
        : 'סימון נוכחות';
      const saveButtonLabel = updateAttendanceOnly
        ? 'שמור נוכחות'
        : 'התחל הרצאה ושמור נוכחות';

      return (
        <Dialog
          header={dialogHeader}
          visible={visible}
          style={{ width: '800px' }}
          onHide={onHide}
          modal
        >
          <div>
            {isLoadingStudents ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                טוען תלמידים...
              </div>
            ) : (
              <>
                <DataTable
                  emptyMessage={
                    <div style={{ textAlign: 'right' }}>לא נמצאו סטודנטים</div>
                  }
                  value={attendanceDataModel}
                  dataKey="student.id"
                  tableStyle={{
                    direction: 'rtl',
                    marginTop: '16px',
                  }}
                >
                  <Column
                    style={{ textAlign: 'right' }}
                    field="student.name"
                    body={(rowData: {
                      student: UserSummaryType;
                      attendanceType?: AttendanceType;
                    }) => (
                      <div>
                        {rowData.student.firstName} {rowData.student.lastName}
                      </div>
                    )}
                    header="שם התלמיד"
                  />

                  <Column
                    style={{ textAlign: 'right' }}
                    field="nationalId"
                    header="ת.ז"
                    body={(rowData: {
                      student: UserSummaryType;
                      attendanceType?: AttendanceType;
                    }) => <div>{rowData.student.nationalId}</div>}
                  />

                  <Column
                    style={{ textAlign: 'center' }}
                    field="attended"
                    header="נוכח"
                    body={(rowData: {
                      student: UserSummaryType;
                      attendanceType?: AttendanceType;
                    }) => (
                      <Checkbox
                        checked={
                          attendanceData.find(
                            (data) => data.studentId === rowData.student.id,
                          )?.attendance === AttendanceType.Attended
                        }
                        onChange={() =>
                          handleAttendanceChange(
                            rowData.student.id,
                            AttendanceType.Attended,
                          )
                        }
                      />
                    )}
                  />
                  <Column
                    style={{ textAlign: 'center' }}
                    field="late"
                    header="איחר"
                    body={(rowData: {
                      student: UserSummaryType;
                      attendanceType?: AttendanceType;
                    }) => (
                      <Checkbox
                        checked={
                          attendanceData.find(
                            (data) => data.studentId === rowData.student.id,
                          )?.attendance === AttendanceType.Late
                        }
                        onChange={() =>
                          handleAttendanceChange(
                            rowData.student.id,
                            AttendanceType.Late,
                          )
                        }
                      />
                    )}
                  />
                  <Column
                    style={{ textAlign: 'center' }}
                    field="missed"
                    header="החסיר"
                    body={(rowData: {
                      student: UserSummaryType;
                      attendanceType?: AttendanceType;
                    }) => (
                      <Checkbox
                        checked={
                          attendanceData.find(
                            (data) => data.studentId === rowData.student.id,
                          )?.attendance === AttendanceType.Missed
                        }
                        onChange={() =>
                          handleAttendanceChange(
                            rowData.student.id,
                            AttendanceType.Missed,
                          )
                        }
                      />
                    )}
                  />
                </DataTable>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'left',
                    marginTop: '16px',
                    gap: '10px',
                  }}
                >
                  <Button
                    style={{ height: '35px' }}
                    severity={'secondary'}
                    outlined={true}
                    label={'ביטול'}
                    onClick={onHide}
                    disabled={isSaving}
                  />
                  <Button
                    style={{ height: '35px' }}
                    label={saveButtonLabel}
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={isSaving || !allStudentsHaveAttendance}
                    tooltip={
                      !allStudentsHaveAttendance
                        ? 'יש לסמן נוכחות לכל התלמידים'
                        : undefined
                    }
                    tooltipOptions={{ showOnDisabled: true }}
                  />
                </div>
              </>
            )}
          </div>
        </Dialog>
      );
    },
  );
